// src/components/Alerts/ClientAlertMessages.js

import React from 'react';
import { Box, Alert, IconButton, Typography } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { ALERT_TYPES } from '../../utils/alertUtils';

/**
 * Get contextual alert content based on alert type
 */
const getAlertContent = (alert, showClientName) => {
  const clientPrefix = showClientName && alert.clientName ? `${alert.clientName}: ` : '';
  
  switch (alert.type) {
    case ALERT_TYPES.STRATEGIC_CONTEXT:
      return {
        message: `${clientPrefix}${alert.message}.`,
        action: 'Update via Questionnaire or Strategic Pulse.'
      };
    case ALERT_TYPES.FINANCIALS:
      return {
        message: `${clientPrefix}${alert.message}.`,
        action: alert.message.includes('ranges') ? 'Update financial ranges.' : 'Upload new financial data.'
      };
    case ALERT_TYPES.ASANA:
      return {
        message: `${clientPrefix}${alert.message}.`,
        action: 'Refresh Asana sync.'
      };
    default:
      return {
        message: `${clientPrefix}Data needs attention.`,
        action: 'Please update.'
      };
  }
};

/**
 * Map ALERT_TYPES to dismissal key names
 */
const getDismissalKey = (alertType) => {
  switch (alertType) {
    case ALERT_TYPES.STRATEGIC_CONTEXT:
      return 'strategic';
    case ALERT_TYPES.FINANCIALS:
      return 'financials';
    case ALERT_TYPES.ASANA:
      return 'asana';
    default:
      return alertType;
  }
};

/**
 * Renders dismissible alert messages
 * Can show alerts for a single client or multiple clients
 */
const ClientAlertMessages = ({ 
  alerts = [], 
  clientId,  // Optional - for single-client mode
  onDismiss,
  showClientName = false
}) => {
  if (alerts.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {alerts.map((alert, index) => {
        const content = getAlertContent(alert, showClientName);
        const dismissalKey = getDismissalKey(alert.type);
        // Use alert's clientId if available, otherwise use prop
        const alertClientId = alert.clientId || clientId;
        
        return (
          <Alert
            key={`${alertClientId}-${alert.type}-${index}`}
            severity="warning"
            sx={{ 
              py: 0.5,
              '& .MuiAlert-message': { 
                flex: 1
              }
            }}
            action={
              <IconButton
                size="small"
                aria-label="dismiss"
                onClick={() => onDismiss(alertClientId, dismissalKey)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            }
          >
            <Typography variant="body2" component="span">
              <strong>{content.message}</strong> {content.action}
            </Typography>
          </Alert>
        );
      })}
    </Box>
  );
};

export default ClientAlertMessages;